/**
 * @file    DefaultCell.tsx
 * @scope   VIEW
 * @summary Legacy table cell: formatted value (+%), Diy Enterprise filter icon and trend arrow.
 * @consumes FilterIcon, cellIndicators, cellFormatting
 * @produces JSX only
 * @sideEffects None.
 */
import React from 'react';
import Tippy from '@tippyjs/react';
import { getIsPercentageFromRow } from 'components/DynamicTable/common/utils/DynamicTableUtils';
import {
  formatCellValue,
  getPreviousField,
  isSingleQuestionGridColumn,
} from 'components/DynamicTable/common/utils/cellFormatting';
import { joinClasses } from 'components/DynamicTable/common/utils/tableLayout';
import {
  getComparisonValueFromRow,
  getSignificanceValueFromRow,
  getValueFromRow,
} from '../../common/utils/cellIndicators';
import FilterIcon from './FilterIcon';

export interface DefaultCellProps {
  row: { [key: string]: any };
  col: any;
  decimalPlaces: any;
  displayTimePeriodDelta: boolean;
}

const DefaultCell: React.FC<DefaultCellProps> = ({
  row,
  col,
  decimalPlaces,
  displayTimePeriodDelta,
}) => {
  const field = `${col.field}`;
  const value = formatCellValue(
    getValueFromRow(row, field),
    field === 'field_0' || isSingleQuestionGridColumn(col),
    decimalPlaces
  );
  const indicator = displayTimePeriodDelta
    ? getComparisonValueFromRow(row, field, getPreviousField(field), decimalPlaces)
    : getSignificanceValueFromRow(row, field);

  const isMetricField = col.field === 'field_1';
  // WHY: the filter tooltip replaces the native title so both don't overlap.
  const title = !row['filterTooltip'] ? value : undefined;
  const isPercentage = col.isPercentage || getIsPercentageFromRow(row, field);

  return (
    <Tippy
      content={
        <div title={title}>
          <div className="tooltip-title">{value}</div>
        </div>
      }
    >
      <>
        <div
          className={joinClasses(
            'pck-card-title',
            'dt-old-cell',
            row['rowType'] === 'L1' && 'l1-row',
            row['rowType'] === 'L2' && 'l2-row',
            isMetricField && 'dynamic-table-cell-with-filter'
          )}
          title={title}
        >
          <span className="dt-old-cell-value" title={title}>
            {isPercentage ? value + '%' : value}
          </span>
          {isMetricField && row['filterTooltip'] && <FilterIcon tooltip={row['filterTooltip']} />}
        </div>
        {indicator}
      </>
    </Tippy>
  );
};

export default DefaultCell;
